
import { useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Descarga las transacciones del comercio (1 fila por cuota) en formato CSV.
 *
 * Útil para que el equipo admin calcule MEL externamente o haga análisis ad-hoc fuera de la app.
 * El backend hace el join SalesTransaction × InstallmentDetail con un limit de 50.000 filas.
 */
export default function TransactionsExportPanel({ companyId }: { companyId: string }) {
  // Default: últimos 6 meses.
  const today = new Date();
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  const isoDate = (d: Date) => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(isoDate(sixMonthsAgo));
  const [to, setTo] = useState(isoDate(today));
  const [commerceCode, setCommerceCode] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setError(null);
    setDownloading(true);
    try {
      const base = getAdminEndpointUrl('companies', 'exportTransactions', { id: companyId });
      const qs = new URLSearchParams();
      qs.set('from', from);
      qs.set('to', to);
      if (commerceCode.trim()) qs.set('commerceCode', commerceCode.trim());

      const { blob, filename } = await adminHttpClient.getBlob(`${base}?${qs.toString()}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? `transacciones-${companyId}-${from}-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.data?.message ?? err?.message ?? 'No se pudo descargar el CSV');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Desde</label>
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Hasta</label>
          <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-9 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Código comercio (opcional)</label>
          <Input value={commerceCode} onChange={(e) => setCommerceCode(e.target.value)} placeholder="Ej. 76123456" className="h-9 text-sm font-mono" />
        </div>
        <div className="flex items-end gap-2">
          <Button
            size="sm" className="h-9 text-xs bg-blue-600 hover:bg-blue-500 gap-1.5 w-full"
            disabled={downloading || !from || !to}
            onClick={handleDownload}
          >
            <span className="material-symbols-outlined text-[16px]">{downloading ? 'progress_activity' : 'download'}</span>
            {downloading ? 'Descargando...' : 'Descargar CSV'}
          </Button>
        </div>
      </div>

      <div className="text-[11px] text-slate-500">
        Formato CSV (1 fila por cuota, separador <code>;</code>). Limit 50.000 filas — si excede, reduce el rango o filtra por código de comercio.
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 text-xs text-red-700 dark:text-red-400">
          <span className="material-symbols-outlined text-[16px] flex-shrink-0">error</span>
          {error}
        </div>
      )}
    </div>
  );
}
